/**
 * Social API Types
 * Types for friends and groups functionality
 */

// Friend status enums
export enum FriendStatus {
  Pending = 0,
  Accepted = 1,
  Declined = 2,
  Blocked = 3,
}

export enum UserFacingFriendStatus {
  NotConnected = 0,
  Pending = 1,
  Connected = 2,
  Blocked = 3,
}

// Friend types
export interface FriendDto {
  id: string
  requesterUserId: string
  receiverUserId: string
  status: FriendStatus
  userFacingStatus: UserFacingFriendStatus
  requestDate: string
  responseDate?: string
  requestedByCurrentUser: boolean
  requesterName?: string
  receiverName?: string
  // Extended user info (may be populated by backend)
  userId?: string
  displayName?: string
  avatar?: string
  username?: string
  addedAt?: string
  mutualFriends?: number
  bio?: string
  location?: string
  stats?: {
    adventuresCompleted?: number
    trailsHiked?: number
    totalDistance?: number
  }
}

export interface FriendRequestDto {
  requestId: string
  userId: string
  displayName: string
  avatar?: string
  username: string
  requestedAt: string
  mutualFriends?: number
  bio?: string
}

// The API returns a flat array of FriendDto, which we need to filter client-side
export type FriendsResponse = FriendDto[]

export interface AddFriendRequest {
  friendId: string
}

export interface BlockUserRequest {
  userId: string
}

// Group visibility enum matching backend
export enum GroupVisibility {
  Public = 0,              // Anyone can see and join the group
  MatchingTagRequired = 1, // Only users with matching tags can see and join
  InviteOnly = 2          // Group is private, invitation required to join
}

// Group permission level enum (hierarchical)
export enum GroupPermissionLevel {
  Anyone = 0,          // No restrictions - anyone can perform this action
  MatchingTags = 1,    // Only users with matching interest tags can perform this action
  Members = 2,         // Only group members can perform this action
  Moderators = 3,      // Moderators and above (Moderator, Admin, Owner)
  Admins = 4,          // Admins and above (Admin, Owner)
  Owner = 5            // Only the group owner
}

// Group permission type enum
export enum GroupPermissionType {
  ViewMembers = 0,
  InviteUsers = 1,
  ViewTags = 2,
  CreateDiscussions = 3,
  ParticipateInDiscussions = 4,
  ViewDiscussions = 5,
  ViewEvents = 6,
  CreateEvents = 7,
}

// Helper to get visibility display name
export function getGroupVisibilityName(visibility: GroupVisibility): string {
  switch (visibility) {
    case GroupVisibility.Public:
      return 'Public'
    case GroupVisibility.MatchingTagRequired:
      return 'Matching Tag Required'
    case GroupVisibility.InviteOnly:
      return 'Invite Only'
    default:
      return 'Public'
  }
}

// Helper to get visibility description
export function getGroupVisibilityDescription(visibility: GroupVisibility): string {
  switch (visibility) {
    case GroupVisibility.Public:
      return 'Anyone can see and join the group'
    case GroupVisibility.MatchingTagRequired:
      return 'Only users with matching interest tags can see and join the group'
    case GroupVisibility.InviteOnly:
      return 'Group is private, only visible to members, invitation required to join'
    default:
      return 'Anyone can see and join the group'
  }
}

// Group role enum matching backend
export enum GroupRole {
  Member = 0,
  Moderator = 1,
  Admin = 2,
  Owner = 3,
}

// Helper to get role display name
export function getGroupRoleName(role: GroupRole): string {
  switch (role) {
    case GroupRole.Member:
      return 'Member'
    case GroupRole.Moderator:
      return 'Moderator'
    case GroupRole.Admin:
      return 'Admin'
    case GroupRole.Owner:
      return 'Owner'
    default:
      return 'Member'
  }
}

// Join request status (numeric enum to match API)
export enum JoinRequestStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  Cancelled = 3,
}

// Group join request DTO
export interface GroupJoinRequestDto {
  id: string
  groupId: string
  groupName?: string
  userId: string
  userName: string
  displayName?: string
  userAvatar?: string | null
  message?: string | null
  status: JoinRequestStatus
  requestedDate: string
  reviewedDate?: string | null
  reviewedBy?: string | null
  reviewNotes?: string | null
}

// Group banned user DTO
export interface GroupBannedUserDto {
  groupId: string
  userId: string | null
  userName: string | null
  userAvatar: string | null
  bannedDate: string | null
  bannedBy: string | null
}

// User group block DTO (for blocking groups from sending invites)
export interface UserGroupBlockDto {
  userId: string | null
  groupId: string
  groupName: string | null
  blockedDate: string
}

// Group types
export interface GroupMemberDto {
  groupId: string
  userId: string
  displayName: string
  userAvatar?: string // API returns userAvatar, not avatar
  userName?: string // API returns userName, not username
  hometown?: string
  reputation?: number
  role: GroupRole
  joinedDate: string // API returns joinedDate, not joinedAt
}

// Group permission settings DTO
export interface GroupPermissionSettingsDto {
  id: string
  groupId: string
  viewMembersLevel: GroupPermissionLevel
  inviteUsersLevel: GroupPermissionLevel
  viewTagsLevel: GroupPermissionLevel
  createDiscussionsLevel: GroupPermissionLevel
  participateInDiscussionsLevel: GroupPermissionLevel
  viewDiscussionsLevel: GroupPermissionLevel
  accessChatLevel: GroupPermissionLevel
  viewEventsLevel?: GroupPermissionLevel
  createEventsLevel?: GroupPermissionLevel
  createdAt: string
  updatedAt?: string
  updatedByUserId?: string
}

export interface GroupDto {
  id: string
  name: string
  description?: string
  fullDescription?: string
  groupImage?: string
  isPublic: boolean // Deprecated - use visibility instead
  visibility?: GroupVisibility // New field replacing isPublic
  maxMembers?: number
  creatorId?: string
  creatorName?: string
  createdDate: string // API returns createdDate, not createdAt
  memberCount: number
  isUserMember: boolean
  userRole?: GroupRole // API returns userRole (enum value)
  isOwner: boolean
  expirationDate?: string
  permissionSettings?: GroupPermissionSettingsDto // NEW: Permission settings
  geofenceCenterLatitude?: number | null
  geofenceCenterLongitude?: number | null
  geofenceRadiusMiles?: number | null
  canAccessChat?: boolean
}

export interface GroupTagDto {
  id: string
  name: string
  displayName: string
  categoryName: string
  categoryDisplayName: string
  icon?: string
  description?: string
  color?: string
}

export interface GroupDetailsDto extends GroupDto {
  members: GroupMemberDto[]
  interestTags?: GroupTagDto[]
  lookingForTags?: GroupTagDto[]
  adventureTypeTags?: GroupTagDto[]
  shareCode?: string | null
  shareCodeExpirationDate?: string | null
  shareCodeMaxUses?: number | null
  shareCodeUsedCount?: number
  isArchived?: boolean
  archivedDate?: string | null
}

export interface GenerateShareCodeRequest {
  maxUses?: number | null
  expirationDate?: string | null
}

export interface ShareCodeResponse {
  shareCode: string
  expirationDate?: string | null
  maxUses?: number | null
  usedCount: number
}

export interface CreateGroupRequest {
  name: string
  description?: string
  fullDescription?: string
  isPublic?: boolean // Deprecated - use visibility instead
  visibility?: GroupVisibility // New field replacing isPublic
  maxMembers?: number
  groupImage?: string
  interestTagIds?: string[]
  lookingForTagIds?: string[]
  adventureTypeTagIds?: string[]
  expirationDate?: string
  permissionSettings?: {
    viewMembersLevel: GroupPermissionLevel
    inviteUsersLevel: GroupPermissionLevel
    viewTagsLevel: GroupPermissionLevel
    createDiscussionsLevel: GroupPermissionLevel
    participateInDiscussionsLevel: GroupPermissionLevel
    viewDiscussionsLevel: GroupPermissionLevel
    accessChatLevel: GroupPermissionLevel
    viewEventsLevel?: GroupPermissionLevel
    createEventsLevel?: GroupPermissionLevel
  }
  geofenceCenterLatitude?: number | null
  geofenceCenterLongitude?: number | null
  geofenceRadiusMiles?: number | null
}

export interface UpdateGroupRequest {
  name?: string
  description?: string
  fullDescription?: string
  isPublic?: boolean // Deprecated - use visibility instead
  visibility?: GroupVisibility // New field replacing isPublic
  maxMembers?: number
  groupImage?: string
  interestTagIds?: string[]
  lookingForTagIds?: string[]
  adventureTypeTagIds?: string[]
  expirationDate?: string
  permissionSettings?: {
    viewMembersLevel: GroupPermissionLevel
    inviteUsersLevel: GroupPermissionLevel
    viewTagsLevel: GroupPermissionLevel
    createDiscussionsLevel: GroupPermissionLevel
    participateInDiscussionsLevel: GroupPermissionLevel
    viewDiscussionsLevel: GroupPermissionLevel
    accessChatLevel: GroupPermissionLevel
    viewEventsLevel?: GroupPermissionLevel
    createEventsLevel?: GroupPermissionLevel
  }
  geofenceCenterLatitude?: number | null
  geofenceCenterLongitude?: number | null
  geofenceRadiusMiles?: number | null
}

export interface AddGroupMemberRequest {
  userId: string
  role?: GroupRole
}

export interface UpdateMemberRoleRequest {
  role: string // API expects "Member" | "Moderator" | "Admin" | "Owner"
}

export interface UpdateGroupPermissionsRequest {
  viewMembersLevel: GroupPermissionLevel
  inviteUsersLevel: GroupPermissionLevel
  viewTagsLevel: GroupPermissionLevel
  createDiscussionsLevel: GroupPermissionLevel
  participateInDiscussionsLevel: GroupPermissionLevel
  viewDiscussionsLevel: GroupPermissionLevel
  accessChatLevel: GroupPermissionLevel
  viewEventsLevel?: GroupPermissionLevel
  createEventsLevel?: GroupPermissionLevel
}

// ====================
// DISCUSSIONS & POSTS
// ====================

export interface DiscussionDto {
  id: string
  title: string
  content: string
  creatorId: string
  creatorName: string
  creatorAvatar?: string
  groupId: string
  groupName: string
  adventureId: string | null
  adventureName: string | null
  adventureCollectionId: string | null
  adventureCollectionName: string | null
  isLocked: boolean
  isPinned: boolean
  createdDate: string
  lastUpdated: string
  postCount: number
  imageId?: string
  imageUrl?: string
}

export interface PostDto {
  id: string
  discussionId: string
  parentPostId?: string
  content: string
  authorId: string
  authorName: string
  authorAvatar?: string
  imageId?: string
  imageUrl?: string
  createdDate: string
  updatedDate: string | null
  isEdited: boolean
  replyCount: number
  replies: PostDto[]
  // Moderation fields
  isHidden: boolean
  hiddenReason?: string
  isMasked: boolean
  hiddenByUserId?: string
  hiddenByDisplayName?: string
  hiddenDate?: string
  // Frontend computed fields
  depth?: number
  canEdit?: boolean
  canDelete?: boolean
}

export interface CreateDiscussionRequest {
  groupId?: string
  adventureId?: string
  adventureCollectionId?: string
  profileUserId?: string
  title: string
  content: string
  permissionLevel?: number
  isPinned?: boolean
  imageId?: string
}

export interface CreatePostRequest {
  discussionId: string
  parentPostId?: string
  content: string
  imageId?: string
}

export interface UpdatePostRequest {
  content: string
}

export interface UploadDiscussionImageResponse {
  imageId: string
  imageUrl: string
  fileSizeBytes: number
  width: number
  height: number
}

export interface FlagPostRequest {
  message: string
  reportTags: string[]
  reportToGroupModerators: boolean
}

// ====================
// SHARE CODE VALIDATION
// ====================

export enum ShareCodeAction {
  Register = 'Register',
  JoinAdventure = 'JoinAdventure',
  UnlockLocation = 'UnlockLocation',
  JoinGroup = 'JoinGroup',
}

export interface ShareCodeValidationResponse {
  isValid: boolean
  action: ShareCodeAction
  codeType: string
  targetId?: string | null
  targetName?: string | null
  message?: string | null
  requiresPermission: boolean
  userHasAccess: boolean
  unmetCriteria: string[]
  expiresAt?: string | null
  remainingUses?: number | null
}

// New sharecode processing types
export type ShareCodeProcessAction =
  | 'AuthenticationRequired'
  | 'GeocacheComplete'
  | 'AlreadyCompleted'
  | 'RequirementFulfilled'
  | 'WrongLocation'
  | 'DiscoveryOnly'
  | 'RewardCodeValid'
  | 'InvalidCode'

export type ShareCodeTargetType = 'Adventure' | 'Cache' | 'Referral' | 'Group' | 'User'

export type ShareCodeType = 'Reward' | 'User' | 'Adventure' | 'Legacy'

export interface RewardInfo {
  instanceId: string
  templateName: string
  description?: string
  remainingUses: number | null
  expiresAt: string | null
  isExpired?: boolean
  canOverrideExpiration?: boolean
  ownerDisplayName?: string
}

export interface ShareCodeProcessResponse {
  success: boolean
  action?: ShareCodeProcessAction
  message?: string
  // Code type identifier
  codeType?: ShareCodeType
  // Geocache/Adventure fields
  adventureId?: string
  adventureName?: string
  sessionId?: string
  creatorReferralCode?: string
  // Workflow fields
  locationId?: string
  locationName?: string
  requirementId?: string
  // Discovery fields
  targetId?: string
  targetType?: ShareCodeTargetType
  targetName?: string
  redirectUrl?: string
  // Reward fields
  canRedeem?: boolean
  rewardInfo?: RewardInfo
  // Error fields
  requiresAdditionalCriteria?: boolean
  shortCode?: string
}

export interface ShareCodeResponseDto {
  shareCode: string
  maxUses: number | null
  expirationDate: string | null
  usedCount: number
  createdAt: string
  creatorId: string
  creatorName: string
}

// ====================
// GROUP SEARCH
// ====================

export enum GroupType {
  Master = 0,
  Temporary = 1,
}

export interface GroupSearchRequest {
  query?: string
  visibility?: GroupVisibility
  groupType?: GroupType
  interestTagIds?: string[]
  lookingForTagIds?: string[]
  adventureTypeTagIds?: string[]
  latitude?: number
  longitude?: number
  radiusMiles?: number
  withinGeofence?: boolean
  excludeJoined?: boolean
  includeArchived?: boolean
  sortBy?: 'name' | 'createdDate' | 'memberCount'
  sortDescending?: boolean
  skip?: number
  take?: number
}

export interface GroupSearchResponse {
  groups: GroupDto[]
  totalCount: number
  skip: number
  take: number
  hasMore: boolean
}
